import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SellerPageContainer } from '@/components/seller/layout/SellerPageContainer';
import { SellerUserCard } from '@/components/seller/mypage/SellerUserCard';
import { SellerMyPageButton } from '@/components/seller/mypage/SellerMyPageButton';
import { LogoutConfirmModal } from '@/components/buyer/LogoutConfirmModal';
import { ContactModal } from '@/components/common/ContactModal';
import { useToast } from '@/components/common/Toast';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { supabase } from '@/lib/supabase';

export function SellerMyPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { showToast, ToastContainer } = useToast();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [sellerData, setSellerData] = useState<any>(null);
  const [restaurantData, setRestaurantData] = useState<any>(null);

  // 구매자인 경우 구매자 마이페이지로 리다이렉트
  useEffect(() => {
    if (user && user.role === 'buyer') {
      navigate('/mypage', { replace: true });
      return;
    }
  }, [user, navigate]);

  // 판매자 및 레스토랑 데이터 가져오기 (판매자만)
  useEffect(() => {
    const fetchSellerData = async () => {
      if (!user?.id || user.role !== 'seller') return;

      try {
        // sellers 테이블에서 데이터 가져오기
        const { data: sellerInfo, error: sellerError } = await supabase
          .from('sellers')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (sellerError) {
          console.error('Failed to fetch seller data:', sellerError);
          return;
        }

        setSellerData(sellerInfo);

        // restaurants 테이블에서 데이터 가져오기
        const { data: restaurantInfo, error: restaurantError } = await supabase
          .from('restaurants')
          .select('*')
          .eq('owner_id', user.id)
          .single();

        if (restaurantError) {
          console.error('Failed to fetch restaurant data:', restaurantError);
          return;
        }

        setRestaurantData(restaurantInfo);
      } catch (error) {
        console.error('Error fetching seller data:', error);
      }
    };

    fetchSellerData();
  }, [user]);

  const handleStoreInfoManagement = () => {
    navigate('/seller/store-edit');
  };

  const handleEvents = () => {
    navigate('/seller/mypage/events');
  };

  const handleNotices = () => {
    navigate('/seller/mypage/notices');
  };

  const handleTermsPolicies = () => {
    navigate('/seller/mypage/terms-policies');
  };

  const handleServiceInfo = () => {
    navigate('/seller/mypage/service-info');
  };

  const handleSettings = () => {
    navigate('/seller/settings');
  };

  const handleLogoutClick = () => {
    setIsLogoutModalOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await signOut();
      setIsLogoutModalOpen(false);
      showToast('로그아웃되었습니다');
      // replace: true를 사용하여 히스토리를 교체하고 구매자 홈으로 이동
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      showToast('로그아웃에 실패했습니다');
    }
  };

  const handleLogoutCancel = () => {
    setIsLogoutModalOpen(false);
  };

  // 구매자인 경우 리다이렉트 중 표시할 로딩 화면
  if (user && user.role === 'buyer') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-[#86DB89] rounded-full animate-spin"></div>
          <p className="text-[#666666] text-base">구매자 마이페이지로 이동 중...</p>
        </div>
      </div>
    );
  }

  return (
    <ProtectedRoute requiredRole="seller" minimalLoading={true}>
      <SellerPageContainer activeNavId="mypage">
        <div className="flex flex-col h-full">
          {/* 헤더 */}
          <header className="flex items-center justify-center py-4 mb-6">
            <h1 className="text-[24px] font-bold text-[#222222]">마이페이지</h1>
          </header>

          {/* 사용자 정보 카드 */}
          {sellerData && restaurantData && (
            <div className="mb-8">
              <SellerUserCard
                storeName={restaurantData.name || '가게명'}
                ownerName={sellerData.name || '대표자명'}
                email={sellerData.email || '이메일'}
                phone={sellerData.phone || '전화번호'}
                joinDate={sellerData.created_at || new Date().toISOString()}
              />
            </div>
          )}

          {/* 메뉴 버튼들 */}
          <div className="flex flex-col gap-1 -mx-4">
            <SellerMyPageButton onClick={handleSettings}>설정</SellerMyPageButton>
            <SellerMyPageButton onClick={handleStoreInfoManagement}>
              가게 정보 관리
            </SellerMyPageButton>
            <SellerMyPageButton disabled>정산</SellerMyPageButton>
            <SellerMyPageButton disabled>쿠폰</SellerMyPageButton>
            <SellerMyPageButton onClick={handleEvents}>이벤트</SellerMyPageButton>
            <SellerMyPageButton onClick={handleNotices}>공지사항</SellerMyPageButton>
            <SellerMyPageButton onClick={() => setShowContactModal(true)}>문의하기</SellerMyPageButton>
            <SellerMyPageButton onClick={handleTermsPolicies}>약관 및 정책</SellerMyPageButton>
            <SellerMyPageButton onClick={handleServiceInfo}>서비스 정보</SellerMyPageButton>
            <SellerMyPageButton onClick={handleLogoutClick}>로그아웃</SellerMyPageButton>
          </div>
        </div>

        {/* 로그아웃 확인 모달 */}
        <LogoutConfirmModal
          isOpen={isLogoutModalOpen}
          onClose={handleLogoutCancel}
          onConfirm={handleLogoutConfirm}
        />

        <ContactModal
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
        />

        <ToastContainer />
      </SellerPageContainer>
    </ProtectedRoute>
  );
}